import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, doc, deleteDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { Save, Trash2, FileText, Upload, Eye, Download, Info } from 'lucide-react';
import Translate from '../components/Translate';

const AdminDownloads = () => {
  const [downloads, setDownloads] = useState([]);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Farmer Registration & Forms');
  const [fileData, setFileData] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(collection(db, 'downloads'), (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDownloads(docs);
      setLoading(false);
    });
    return unsub;
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      Swal.fire({
        title: 'Invalid File Type',
        text: 'Please select a PDF document only.',
        icon: 'error',
        confirmButtonColor: '#2E7D32'
      });
      e.target.value = '';
      return;
    }

    if (file.size > 800 * 1024) {
      Swal.fire({
        title: 'File Too Large',
        text: 'Please upload a PDF document smaller than 800 KB.',
        icon: 'error',
        confirmButtonColor: '#2E7D32'
      });
      e.target.value = '';
      return;
    }

    const sizeStr = file.size > 1024 * 1024 
      ? (file.size / (1024 * 1024)).toFixed(1) + ' MB' 
      : (file.size / 1024).toFixed(0) + ' KB';

    setFileName(file.name);
    setFileSize(sizeStr);

    const reader = new FileReader();
    reader.onload = (event) => {
      setFileData(event.target.result);
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'PDF processed successfully',
        showConfirmButton: false,
        timer: 1500
      });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      Swal.fire('Error', 'Please enter a document title.', 'error');
      return;
    }
    if (!fileData) {
      Swal.fire('Error', 'Please upload a valid PDF document.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await addDoc(collection(db, 'downloads'), {
        title: title.trim(),
        category,
        fileData,
        fileName,
        fileSize,
        fileType: 'PDF',
        createdAt: serverTimestamp()
      });

      Swal.fire({
        title: 'Success!',
        text: 'Document uploaded successfully.',
        icon: 'success',
        confirmButtonColor: '#2E7D32'
      });

      // Reset form
      setTitle('');
      setFileData('');
      setFileName('');
      setFileSize('');
      const fileInput = document.getElementById('pdf-file-input');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      console.error('Error saving document:', error);
      Swal.fire('Error', 'Could not save the document. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, docTitle) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete "${docTitle}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#2E7D32',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteDoc(doc(db, 'downloads', id));
          Swal.fire('Deleted!', 'Document has been removed.', 'success');
        } catch (error) {
          console.error('Error deleting document:', error);
          Swal.fire('Error', 'Could not delete the document. Please try again.', 'error');
        }
      }
    });
  };

  const viewPdf = (base64Data) => {
    const newTab = window.open();
    if (newTab) {
      newTab.document.write(`<iframe src="${base64Data}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>`);
    } else {
      Swal.fire('Popup Blocked', 'Please allow popups to view this PDF.', 'warning');
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto space-y-10 font-body">
      <div>
        <h1 className="text-3xl font-heading font-extrabold text-charcoal">
          <Translate>Download Documents Manager</Translate>
        </h1>
        <p className="text-slate text-sm mt-1">
          <Translate>Upload and organize PDF documents for farmer forms, policies, and certifications.</Translate>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Upload Form */}
        <form onSubmit={handleSubmit} className="lg:col-span-4 bg-white p-8 rounded-2xl border border-gray-150 shadow-premium space-y-6">
          <h2 className="text-xl font-heading font-bold text-charcoal flex items-center gap-2">
            <Upload className="text-emerald" size={20} />
            <span><Translate>Add New Document</Translate></span>
          </h2>
          <div className="gold-divider"></div>

          <div className="space-y-2">
            <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider">
              <Translate>Document Title</Translate>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Drip Irrigation Form"
              className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider">
              <Translate>Category / Section</Translate>
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full bg-ivory border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 text-sm"
            >
              <option value="Farmer Registration & Forms">Farmer Registration & Forms</option>
              <option value="Corporate Reports & Policies">Corporate Reports & Policies</option>
              <option value="Certifications & Compliance">Certifications & Compliance</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-heading font-bold text-slate uppercase tracking-wider">
              <Translate>PDF Document</Translate>
            </label>
            <div className="border-2 border-dashed border-gray-200 hover:border-emerald/50 rounded-2xl p-6 transition-all duration-300 bg-ivory/50 flex flex-col items-center justify-center text-center cursor-pointer relative group">
              <input
                id="pdf-file-input"
                type="file"
                accept="application/pdf"
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <FileText className="text-gray-400 group-hover:text-emerald transition-colors mb-3" size={32} />
              {fileName ? (
                <div className="text-xs text-charcoal font-semibold space-y-1">
                  <p className="line-clamp-1">{fileName}</p>
                  <p className="text-slate text-[10px]">{fileSize}</p>
                </div>
              ) : (
                <div className="text-xs text-slate">
                  <p className="font-bold text-charcoal"><Translate>Click to select PDF</Translate></p>
                  <p className="text-[10px] mt-1"><Translate>Max Size: 800 KB</Translate></p>
                </div>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full btn-premium flex items-center justify-center gap-2 py-4 cursor-pointer"
          >
            <Save size={16} />
            <span>{submitting ? <Translate>Uploading...</Translate> : <Translate>Save Document</Translate>}</span>
          </button>
        </form>

        {/* Existing Documents List */}
        <div className="lg:col-span-8 bg-white p-8 rounded-2xl border border-gray-150 shadow-premium space-y-6">
          <h2 className="text-xl font-heading font-bold text-charcoal flex items-center gap-2">
            <FileText className="text-emerald" size={20} />
            <span><Translate>Active Documents</Translate></span>
          </h2>
          <div className="gold-divider"></div>

          {loading ? (
            <div className="text-center py-10 text-slate">
              <Translate>Loading documents...</Translate>
            </div>
          ) : downloads.length === 0 ? (
            <div className="text-center py-10 text-slate flex flex-col items-center gap-2">
              <Info size={24} className="text-slate/40" />
              <p><Translate>No custom documents uploaded yet.</Translate></p>
              <p className="text-xs text-slate/50"><Translate>Default files will display on the user downloads page.</Translate></p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 text-xs text-slate uppercase tracking-wider font-heading font-bold">
                    <th className="pb-3"><Translate>Title</Translate></th>
                    <th className="pb-3"><Translate>Category</Translate></th>
                    <th className="pb-3"><Translate>Size</Translate></th>
                    <th className="pb-3 text-right"><Translate>Actions</Translate></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 text-sm">
                  {downloads.map((doc) => (
                    <tr key={doc.id} className="hover:bg-ivory/20 transition-colors group">
                      <td className="py-4 font-heading font-bold text-charcoal">
                        {doc.title}
                      </td>
                      <td className="py-4 text-xs">
                        <span className="bg-sage/10 text-sage-dark px-2.5 py-1 rounded-full font-semibold">
                          {doc.category}
                        </span>
                      </td>
                      <td className="py-4 text-slate text-xs">
                        {doc.fileSize}
                      </td>
                      <td className="py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => viewPdf(doc.fileData)}
                            className="p-2 bg-mist text-slate hover:bg-emerald hover:text-white rounded-lg transition-colors cursor-pointer"
                            title="View PDF"
                          >
                            <Eye size={14} />
                          </button>
                          <a
                            href={doc.fileData}
                            download={doc.fileName || `${doc.title}.pdf`}
                            className="p-2 bg-mist text-slate hover:bg-emerald hover:text-white rounded-lg transition-colors flex items-center justify-center cursor-pointer"
                            title="Download PDF"
                          >
                            <Download size={14} />
                          </a>
                          <button
                            onClick={() => handleDelete(doc.id, doc.title)}
                            className="p-2 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-colors cursor-pointer"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDownloads;
